import React, { useState } from "react";
import { useData } from "../../context/DataContext";
import Card from "../ui/Card";
import Table from "../ui/Table";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

const UploadPlaybookModal = ({ isOpen, onClose }) => {
  const { players, uploadPlaybook } = useData();
  const [playbookName, setPlaybookName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadedPlaybook, setUploadedPlaybook] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [install, setInstall] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ];
      if (!validTypes.includes(file.type)) {
        setUploadError(
          "Please select a PDF, Word document, or PowerPoint file"
        );
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setUploadError("File size must be less than 10MB");
        return;
      }

      setUploadError(null);
      setSelectedFile(file);
      if (!playbookName) {
        setPlaybookName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handlePlayerToggle = (playerId) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlayers.length === players.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(players.map((p) => p.id));
    }
  };

  const handleUpload = async () => {
    if (playbookName && selectedFile) {
      setIsUploading(true);
      setUploadError(null);

      try {
        const newPlaybook = await uploadPlaybook(
          selectedFile,
          playbookName,
          selectedPlayers,
          install
        );
        setUploadedPlaybook(newPlaybook);
        setShowConfirmation(true);
      } catch (error) {
        setUploadError(error.message || "Failed to upload playbook");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleClose = () => {
    setPlaybookName("");
    setSelectedFile(null);
    setSelectedPlayers([]);
    setShowConfirmation(false);
    setUploadedPlaybook(null);
    setIsUploading(false);
    setUploadError(null);
    setInstall("");
    onClose();
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    handleClose();
  };

  const selectedPlayerNames = selectedPlayers
    .map((id) => players.find((p) => p.id === id)?.name)
    .filter(Boolean);

  if (showConfirmation) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleConfirmationClose}
        title="Upload Successful"
      >
        <div className="success-modal-content">
          <div className="success-icon">✓</div>
          <h3 className="success-title">Playbook Uploaded Successfully!</h3>
          <div className="success-details">
            <p>
              <strong>Playbook:</strong> {uploadedPlaybook?.title}
            </p>
            {uploadedPlaybook?.install && (
              <p>
                <strong>Install Version:</strong> {uploadedPlaybook.install}
              </p>
            )}
            <p>
              <strong>Assigned to:</strong>{" "}
              {selectedPlayers.length > 0
                ? selectedPlayerNames.join(", ")
                : "No players assigned"}
            </p>
            <p>
              <strong>Upload Date:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>
          <Button variant="primary" onClick={handleConfirmationClose}>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload New Playbook"
      size="lg"
    >
      <div>
        {uploadError && (
          <div className="form-submit-error" style={{ marginBottom: "1rem" }}>
            <p className="form-submit-error-text">{uploadError}</p>
          </div>
        )}

        {/* File Upload Section */}
        <div className="section-container">
          <h4 className="section-title">Upload File</h4>
          <div className="form-group">
            <label className="form-label">Select Playbook File</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx"
              onChange={handleFileChange}
              className="form-input"
            />
            {selectedFile && (
              <p className="form-file-selected">
                Selected: {selectedFile.name}
              </p>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Playbook Name</label>
            <input
              type="text"
              value={playbookName}
              onChange={(e) => setPlaybookName(e.target.value)}
              placeholder="Enter playbook name"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Install Version (Optional)</label>
            <input
              type="number"
              value={install}
              onChange={(e) => setInstall(e.target.value)}
              placeholder="Enter install version (e.g., 1.0, 2.1)"
              className="form-input"
              min="0"
              step="0.1"
            />
          </div>
        </div>

        {/* Player Selection */}
        <div className="player-selection-container">
          <div className="player-selection-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '0.75rem',
            padding: '0 0.25rem'
          }}>
            <h4 className="section-title" style={{ marginBottom: 0, fontSize: '0.925rem', color: '#334155' }}>Assign to Players</h4>
            {players.length > 0 && (
              <button
                type="button"
                onClick={handleSelectAll}
                className={`select-all-btn ${selectedPlayers.length === players.length ? 'all-selected' : ''}`}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: selectedPlayers.length === players.length ? '#eff6ff' : 'white', 
                  border: `1px solid ${selectedPlayers.length === players.length ? '#3b82f6' : '#e2e8f0'}`, 
                  color: selectedPlayers.length === players.length ? '#2563eb' : '#64748b', 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  cursor: 'pointer',
                  padding: '5px 10px',
                  borderRadius: '20px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: selectedPlayers.length === players.length ? '0 1px 2px rgba(37, 99, 235, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
                onMouseOver={(e) => {
                  if (selectedPlayers.length !== players.length) {
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.color = '#2563eb';
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedPlayers.length !== players.length) {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#64748b';
                    e.currentTarget.style.backgroundColor = 'white';
                  }
                }}
              >
                <span style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '14px',
                  height: '14px',
                  borderRadius: '50%',
                  border: `1px solid ${selectedPlayers.length === players.length ? '#2563eb' : '#cbd5e1'}`,
                  backgroundColor: selectedPlayers.length === players.length ? '#2563eb' : 'transparent',
                  color: 'white',
                  fontSize: '8px'
                }}>
                </span>
                {selectedPlayers.length === players.length ? "Deselect All" : "Select All"}
              </button>
            )}
          </div>
          <p className="text-sm text-muted mb-3">
            Select players to assign this playbook immediately, or leave
            unselected to assign later.
          </p>
          <div className="player-list-container">
            {players.map((player) => (
              <label
                key={player.id}
                className={`player-item ${
                  selectedPlayers.includes(player.id) ? "selected" : ""
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedPlayers.includes(player.id)}
                  onChange={() => handlePlayerToggle(player.id)}
                  className="player-item-checkbox"
                />
                <div>
                  <div className="player-item-name">{player.name}</div>
                  <div className="player-item-email">{player.email}</div>
                </div>
              </label>
            ))}
          </div>
          {selectedPlayers.length > 0 && (
            <p className="text-sm text-primary mt-2">
              {selectedPlayers.length} player
              {selectedPlayers.length !== 1 ? "s" : ""} selected
            </p>
          )}
        </div>

        <div className="modal-buttons">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={!playbookName || !selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <div className="spinner spinner-small"></div>
                Uploading...
              </>
            ) : selectedPlayers.length > 0 ? (
              "Upload & Assign"
            ) : (
              "Upload Playbook"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const RemovePlaybookModal = ({ isOpen, onClose, selectedPlaybook }) => {
  const { removePlaybook, getPlaybookPlayers } = useData();
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState("");

  const handleRemove = async () => {
    if (!selectedPlaybook) return;

    setIsRemoving(true);
    setError("");

    try {
      await removePlaybook(selectedPlaybook.id);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to remove playbook. Please try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClose = () => {
    if (!isRemoving) {
      setError("");
      onClose();
    }
  };

  if (!selectedPlaybook) return null;

  const assignedPlayers = getPlaybookPlayers(selectedPlaybook.id);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Remove Playbook">
      <div>
        <p className="helper-text">
          Are you sure you want to remove <strong>{selectedPlaybook.title}</strong>?
        </p>
        <p className="helper-text" style={{ marginTop: "0.5rem", color: "#dc2626" }}>
          This will permanently delete the playbook from your account and remove it from all assigned players.
        </p>
        {assignedPlayers.length > 0 && (
          <p className="helper-text" style={{ marginTop: "0.5rem", fontWeight: 500 }}>
            This playbook is currently assigned to {assignedPlayers.length} player{assignedPlayers.length !== 1 ? "s" : ""}.
          </p>
        )}

        {error && (
          <div className="form-submit-error" style={{ marginTop: "1rem" }}>
            <p className="form-submit-error-text">{error}</p>
          </div>
        )}

        <div className="modal-footer-custom" style={{ marginTop: "1.5rem" }}>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRemove}
            disabled={isRemoving}
          >
            {isRemoving ? (
              <>
                <div className="spinner spinner-small"></div>
                Removing...
              </>
            ) : (
              "Remove Playbook"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const PlaybookManager = () => {

  const { playbooks, getPlaybookPlayers } = useData();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [isViewerLoading, setIsViewerLoading] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [playbookToRemove, setPlaybookToRemove] = useState(null);

  const handleRemoveClick = (playbook) => {
    setPlaybookToRemove(playbook);
    setIsRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);
    setPlaybookToRemove(null);
  };

  function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const columns = [
    {
      header: "Playbook Name",
      accessor: "title",
      width: "28%",
    },
    {
      header: "Install Version",
      accessor: "install",
      width: "12%",
      render: (playbook) => {
        return playbook.install ? `v${playbook.install}` : "—";
      },
    },
    {
      header: "Assigned Players",
      accessor: "assignedPlayers",
      width: "20%",
      render: (playbook) => {
        const assignedPlayers = getPlaybookPlayers(playbook.id);
        return (
          <div>
            <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>
              {assignedPlayers.length} player
              {assignedPlayers.length !== 1 ? "s" : ""}
            </div>
            <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
              {assignedPlayers
                .slice(0, 2)
                .map((p) => p.name)
                .join(", ")}
              {assignedPlayers.length > 2 &&
                ` +${assignedPlayers.length - 2} more`}
            </div>
          </div>
        );
      },
    },
    {
      header: "Upload Date",
      accessor: "uploadDate",
      width: "12%",
      render: (playbook) => {
        const date = playbook.uploadDate;
        if (typeof date === "string") {
          return new Date(date).toLocaleDateString();
        }
        return date
          ? new Date(date.seconds * 1000).toLocaleDateString()
          : "N/A";
      },
    },
    {
      header: "File Size",
      accessor: "fileSize",
      width: "13%",
      render: (playbook) => {
        if (playbook.fileSize) {
          return formatFileSize(playbook.fileSize);
        }
        return (
          <span className="text-muted">
            {(Math.random() * 5 + 1).toFixed(1)} MB
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "actions",
      width: "15%",
      render: (playbook) => {
        const url = playbook.pdfUrl || null;

        const handleView = () => {
          if (!url) return;
          setViewerUrl(url);
          setIsViewerLoading(true);
          setIsViewerOpen(true);
        };

        return (
          <div className="playbook-actions">
            <Button variant="secondary" onClick={handleView} disabled={!url}>
              View
            </Button>
            <Button 
              variant="danger" 
              onClick={() => handleRemoveClick(playbook)}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  const headerActions = (
    <Button variant="primary" onClick={() => setIsUploadModalOpen(true)}>
      + Upload Playbook
    </Button>
  );

  return (
    <>
      <Card title="Playbooks" headerActions={headerActions}>
        {playbooks.length === 0 ? (
          <div className="empty-state-container">
            <div className="engagement-icon">📚</div>
            <h3 className="engagement-title">No playbooks yet</h3>
            <p>Upload your first playbook to get started</p>
          </div>
        ) : (
          <Table columns={columns} data={playbooks} />
        )}
      </Card>

      <Modal
        isOpen={isViewerOpen}
        onClose={() => {
          setIsViewerOpen(false);
          setViewerUrl(null);
        }}
        title="View Playbook"
        size="xl"
      >
        <div style={{ height: "75vh" }}>
          {viewerUrl ? (
            <div className="playbook-viewer-wrapper">
              {isViewerLoading && (
                <div className="playbook-viewer-overlay">
                  <div className="spinner" />
                </div>
              )}

              <iframe
                src={viewerUrl}
                title="Playbook Viewer"
                className="playbook-viewer-iframe"
                onLoad={() => setIsViewerLoading(false)}
                onError={() => setIsViewerLoading(false)}
              />
            </div>
          ) : (
            <div>No preview available</div>
          )}
        </div>
      </Modal>

      <UploadPlaybookModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />

      <RemovePlaybookModal
        isOpen={isRemoveModalOpen}
        onClose={closeRemoveModal}
        selectedPlaybook={playbookToRemove}
      />
    </>
  );

};

export default PlaybookManager;
